import { Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { z } from 'zod'
import { useAuthStore } from '@/application/stores/auth.store'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/common/Input'
import { Label } from '@/ui/components/common/Label'
import { SocialLoginButtons } from '@/ui/components/public/SocialLoginButtons'
import { apiClient } from '@/infrastructure/api/client'
import type { User } from '@/domains/types'
import { RecaptchaNotice } from './RecaptchaNotice'

const makeRegisterSchema = (translate: (key: string, opts?: Record<string, unknown>) => string) =>
  z.object({
    username: z
      .string()
      .min(3, translate('validation.username_min'))
      .max(30, translate('validation.username_max')),
    email: z.string().email(translate('validation.email_invalid')),
    password: z.string().min(8, translate('validation.password_min')),
    displayName: z.string().optional(),
  })
type RegisterFormData = z.infer<ReturnType<typeof makeRegisterSchema>>

/**
 * Account creation card: owns the registration form (zod schema, RHF state,
 * submit handler that calls /auth/register, logs the user in and redirects to
 * the public homepage), the reCAPTCHA v3 token retrieval, the social signup
 * buttons and the "already have an account?" footer link.
 */
export function RegisterFormSection() {
  const { t } = useTranslation()
  const schema = useMemo(() => makeRegisterSchema(t), [t])
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [pending, setPending] = useState(false)

  // reCAPTCHA v3 — `executeRecaptcha` is undefined when no provider is present
  // (i.e. when VITE_RECAPTCHA_SITE_KEY is not set) so we skip captcha safely.
  const { executeRecaptcha } = useGoogleReCaptcha()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({ resolver: zodResolver(schema) })

  const onSubmit = useCallback(
    async (data: RegisterFormData) => {
      try {
        // Obtain reCAPTCHA v3 token only when the provider is actually configured.
        // When VITE_RECAPTCHA_SITE_KEY is absent the GoogleReCaptchaProvider is not
        // mounted and the default context throws — so we guard the call explicitly.
        let captchaToken: string | undefined
        if (import.meta.env.VITE_RECAPTCHA_SITE_KEY && executeRecaptcha) {
          captchaToken = await executeRecaptcha('register')
        }

        const response = await apiClient.post<
          | {
              user: Omit<User, 'createdAt' | 'updatedAt'>
              accessToken: string
              refreshToken: string
            }
          | { pending: true }
        >('/auth/register', {
          username: data.username,
          email: data.email,
          password: data.password,
          displayName: data.displayName || undefined,
          captchaToken,
        })

        if ('pending' in response.data && response.data.pending) {
          setPending(true)
          return
        }

        if ('accessToken' in response.data) {
          const timestamp = new Date().toISOString()
          const fullUser: User = {
            ...response.data.user,
            createdAt: timestamp,
            updatedAt: timestamp,
          }
          login(fullUser, response.data.accessToken, response.data.refreshToken)
          void navigate({ to: '/' })
        }
      } catch {
        setError('root', { message: t('common.error') })
      }
    },
    [executeRecaptcha, login, navigate, setError, t],
  )

  if (pending) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 text-center space-y-3">
        <div className="text-4xl">✅</div>
        <h2 className="text-lg font-semibold">{t('auth.register_pending_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('auth.register_pending_body')}</p>
      </div>
    )
  }

  return (
    <>
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        {errors.root && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-3 py-2">
            {errors.root.message}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="username">{t('auth.username')}</Label>
            <Input
              id="username"
              {...register('username')}
              className="mt-1"
              autoComplete="username"
            />
            {errors.username && (
              <p className="text-destructive text-xs mt-1">{errors.username.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              className="mt-1"
              autoComplete="email"
            />
            {errors.email && (
              <p className="text-destructive text-xs mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="password">{t('auth.password')}</Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              className="mt-1"
              autoComplete="new-password"
            />
            {errors.password && (
              <p className="text-destructive text-xs mt-1">{errors.password.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="displayName">{t('auth.display_name')}</Label>
            <Input
              id="displayName"
              {...register('displayName')}
              className="mt-1"
              autoComplete="name"
            />
            {errors.displayName && (
              <p className="text-destructive text-xs mt-1">{errors.displayName.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" isLoading={isSubmitting}>
            {t('auth.register_button')}
          </Button>

          <RecaptchaNotice />
        </form>

        {/* Social registration — same OAuth flow, always redirects to public homepage */}
        <SocialLoginButtons mode="register" />
      </div>

      <p className="text-center text-sm text-muted-foreground">
        {t('auth.has_account')}{' '}
        <Link to="/login" className="text-primary hover:underline">
          {t('auth.login_link')}
        </Link>
      </p>
    </>
  )
}
